import { useEffect, useState } from "react";
import { api, ApiFailure } from "../api";
import { ChessBoard } from "../components/ChessBoard";
import { PIECE_SRC } from "../components/pieces";
import { GiveUp } from "../components/GiveUp";
import { ResultSheet } from "../components/ResultSheet";
import { Centered, Header, Shell } from "../components/Shell";
import { Toast } from "../components/Toast";
import { haptic } from "../telegram";
import { useGame } from "../useGame";

interface View {
  fen: string;
  color: "w" | "b";
  mateIn: number;
  rating: number;
  lives: number;
  maxLives: number;
  solved: string[];
  replies: string[];
  check: boolean;
  moves: Record<string, string[]>;
  solution?: string[];
  startFen?: string;
  url?: string;
  altMate?: boolean;
}

const COPY = {
  bad_move: "Не той хід",
  illegal_move: "Так не ходять",
};

const PROMO = [
  { p: "q", name: "ферзь" },
  { p: "r", name: "тура" },
  { p: "b", name: "слон" },
  { p: "n", name: "кінь" },
];

export function Mat({ date }: { date?: string | undefined }) {
  const g = useGame<View>("mat", COPY, date);
  const [selected, setSelected] = useState<string | null>(null);
  const [wrongSquare, setWrongSquare] = useState<string | null>(null);
  const [promo, setPromo] = useState<{ from: string; to: string } | null>(null);

  const state = g.screen.kind === "game" ? g.screen.state : null;
  const finished = state !== null && state.status !== "playing";

  // Clear the selection whenever the position changes under us.
  useEffect(() => {
    setSelected(null);
  }, [state?.view.fen]);

  if (g.screen.kind === "loading") return <Shell><Centered>…</Centered></Shell>;
  if (g.screen.kind === "error") {
    return (
      <Shell>
        <Centered>
          <div>{g.screen.message}</div>
          {g.screen.retry && (
            <button type="button" onClick={() => void g.load()} className="rounded-xl bg-tgbutton px-5 py-2.5 text-sm font-semibold text-tgbuttontext">
              ще раз
            </button>
          )}
        </Centered>
      </Shell>
    );
  }
  const s = g.screen.state;
  const v = s.view;
  const won = s.status === "won";
  const lastReply = v.replies.at(-1);
  const lastMove = lastReply ? { from: lastReply.slice(0, 2), to: lastReply.slice(2, 4) } : null;
  const remaining = v.mateIn - v.solved.length;

  const send = async (uci: string, fromSquare: string) => {
    const next = await g.run(() => api.guess<View>("mat", uci, date));
    if (!next) return;
    const extra = next.extra as { right?: boolean } | undefined;
    if (extra?.right === false) {
      setWrongSquare(fromSquare);
      haptic.error();
      g.showToast("Не мат — мінус життя");
      window.setTimeout(() => setWrongSquare(null), 700);
    }
  };

  const onMove = (from: string, to: string) => {
    const dests = v.moves[from] ?? [];
    // Promotion when the only listed destinations for `to` carry a suffix.
    if (dests.includes(`${to}q`) && !dests.includes(to)) {
      setSelected(null);
      setPromo({ from, to });
      return;
    }
    setSelected(null);
    void send(from + to, from);
  };

  return (
    <Shell
      header={
        <Header
          left={date ? `📼 ${date}` : `#${s.number}`}
          title="МАТ ДНЯ"
          right={date ? "" : `🔥 ${s.me.streak}`}
        />
      }
    >
      <div className="flex flex-1 flex-col justify-center gap-3 px-3">
        <div className="flex items-center justify-between px-1 text-sm">
          <span className="font-semibold">
            {v.color === "w" ? "⚪ білі" : "⚫ чорні"} — мат за {remaining > 0 ? remaining : v.mateIn}
          </span>
          <span title="життя">
            {"❤️".repeat(Math.max(0, v.lives))}
            {"🖤".repeat(Math.max(0, v.maxLives - v.lives))}
          </span>
        </div>
        <ChessBoard
          fen={v.fen}
          orientation={v.color}
          moves={v.moves}
          selected={selected}
          onSelect={setSelected}
          onMove={onMove}
          lastMove={lastMove}
          disabled={finished || g.busy}
          wrongSquare={wrongSquare}
        />
        <div className="text-center text-xs text-tghint">
          {finished
            ? won
              ? "мат!"
              : "гру завершено"
            : v.check
              ? "шах — але треба мат"
              : `тицяй фігуру, потім клітинку · складність ${v.rating}`}
        </div>
        {!finished && (
          <div className="flex px-1">
            <GiveUp busy={g.busy} onGiveUp={() => void g.run(() => api.giveUp<View>("mat", date))} />
          </div>
        )}
      </div>

      {promo && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/60" onClick={() => setPromo(null)}>
          <div className="rounded-2xl bg-tgsection p-4" onClick={(e) => e.stopPropagation()}>
            <div className="mb-2 text-center text-sm font-semibold">У кого перетворити?</div>
            <div className="flex gap-2">
              {PROMO.map(({ p, name }) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => {
                    const move = promo;
                    setPromo(null);
                    void send(`${move.from}${move.to}${p}`, move.from);
                  }}
                  className="flex h-16 w-16 flex-col items-center justify-center rounded-xl bg-[rgba(128,128,128,0.2)] active:opacity-70"
                  aria-label={name}
                >
                  <img
                    src={PIECE_SRC[v.color === "w" ? p.toUpperCase() : p]}
                    alt={name}
                    draggable={false}
                    className="h-10 w-10"
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <Toast message={g.toast} />
      {g.sheetOpen && finished && (
        <ResultSheet
          state={s}
          archive={Boolean(date)}
          title={won ? (v.lives === v.maxLives ? "Чисто!" : "Мат!") : "Не знайшов"}
          subtitle={
            won
              ? `${"❤️".repeat(v.lives)}${v.altMate ? " · свій варіант мату" : ""} · стрік ${s.me.streak} 🔥`
              : "стрік згорів"
          }
          scoreLabel="avg втрат"
          formatScore={(n) => `${n}`}
          onDismiss={() => g.setSheetOpen(false)}
        >
          {v.solution && (
            <div className="mt-4 rounded-xl bg-[rgba(128,128,128,0.12)] px-4 py-3 text-center">
              <div className="text-xs uppercase tracking-wide text-tghint">розв'язок</div>
              <div className="mt-1 font-mono text-sm">{v.solution.join(" ")}</div>
              {v.url && (
                <a
                  href={v.url}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-2 inline-block text-xs text-tgbutton underline"
                >
                  розібрати на lichess ↗
                </a>
              )}
            </div>
          )}
        </ResultSheet>
      )}
    </Shell>
  );
}
